import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import type { Locale } from '@/i18n'
import { formatNumber } from '@/lib/format'
import { CellValue } from '@/lists/cellValue'
import { HistoryPanel } from './HistoryPanel'
import { Feed, Panel, StateView, type MenuAction, type StageEnd, type StageStep } from './record'
import { feedItemOf, formatMoment, listPath, type Translate } from './recordHelpers'
import { TimelineMore, useTimeline } from './useTimeline'
import '@/styles/warehouse-docs.css'

/**
 * Общие части раскладок складских документов (Receiving, Shipment, Picking, Inventory,
 * Transfer.dc.html).
 *
 * Пять карточек устроены одинаково: шапка с действием по статусу документа, полоса этапов,
 * факты, вкладки. Различаются данными и переходами — их держит каждая раскладка, а здесь
 * то, что иначе было бы написано пять раз и разошлось бы: как рисуется количество, как
 * называется отказ сервера, откуда берутся имена товаров и коды ячеек.
 *
 * **Ни одна складская цифра здесь не считается заново.** Расхождение, потеря в пути,
 * свободный остаток приходят с сервера; клиент складывает только то, что сам показывает
 * строками (итог колонки), и сверяет строку с её формулой ради подсветки.
 */

/**
 * Количество на экране. `null` — прочерк, а не ноль: «неизвестно» и «ноль» на складе
 * разные утверждения, и разница между ними стоит инвентаризации.
 */
export function qty(value: number | null | undefined, locale: Locale): string {
  if (value == null || value === undefined) return '—'
  return formatNumber(value, locale)
}

/** Количество со знаком: расхождение без знака не говорит, в какую сторону разошлось. */
export function signedQty(value: number, locale: Locale): string {
  const text = formatNumber(Math.abs(value), locale)
  if (value > 0) return `+${text}`
  if (value < 0) return `−${text}`
  return text
}

/** Короткая форма идентификатора — когда имени нет: полный UUID в ячейке нечитаем. */
export function short(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—'
}

/**
 * Ключ идемпотентности операции. Рождается при открытии подтверждения и живёт до успеха:
 * повтор после потерянного ответа уходит тем же ключом, и сервер отдаёт сохранённый ответ.
 */
export function newOperationKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * Результат операции документа: отказ сервера — его текстом, успех — словами.
 *
 * Отказ не заменяется общим «ошибка»: домен отвечает, почему нельзя («остались
 * непосчитанные позиции»), и только этот текст говорит, что делать дальше.
 */
export function useOperation(invalidate: unknown[][]) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const keys = JSON.stringify(invalidate)

  const refresh = useCallback(() => {
    for (const key of JSON.parse(keys) as unknown[][]) {
      void queryClient.invalidateQueries({ queryKey: key })
    }
  }, [queryClient, keys])

  const succeed = useCallback(
    (message: string) => {
      setFailure(null)
      setNotice(message)
      refresh()
    },
    [refresh],
  )

  const fail = useCallback(
    (error: unknown) => {
      setNotice(null)
      setFailure(error instanceof ApiError ? error.message : t('app.error'))
    },
    [t],
  )

  const clear = useCallback(() => {
    setFailure(null)
    setNotice(null)
  }, [])

  return { failure, notice, succeed, fail, clear, setFailure }
}

/** Полоса сообщений операции под шапкой. */
export function OperationAlerts({ failure, notice }: { failure: string | null; notice: string | null }) {
  return (
    <>
      {failure && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{failure}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          <span>{notice}</span>
        </div>
      )}
    </>
  )
}

type DocQuery = { isLoading: boolean; error: unknown; refetch: () => unknown }

/**
 * Загрузка, отказ и ошибка документа — как в макетах: скелет, «Нет доступа», «Ошибка» с
 * повтором. `null` — документ на месте, рисовать карточку.
 */
export function documentState(query: DocQuery, entity: string, fallbackPath: string, t: Translate): ReactNode | null {
  if (query.isLoading) {
    return (
      <div className="rec-page" aria-busy="true" aria-label={t('app.loading')}>
        <span className="skel" style={{ width: 360, height: 20 }} />
        <span className="skel" style={{ height: 44, borderRadius: 6 }} />
        <span className="skel" style={{ height: 70, borderRadius: 8 }} />
        <span className="skel" style={{ height: 220, borderRadius: 8 }} />
      </div>
    )
  }
  if (query.error) {
    const forbidden = query.error instanceof ApiError && query.error.isForbidden
    return (
      <StateView
        boxed
        kind={forbidden ? 'forbidden' : 'error'}
        title={forbidden ? t('record.state.forbidden.title') : t('record.state.error.title')}
        description={forbidden ? t('record.state.forbidden.text') : t('record.state.error.text')}
        actions={
          <>
            {!forbidden && (
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void query.refetch()}>
                <Icon name="refresh" size="sm" />
                {t('record.state.retry')}
              </button>
            )}
            <Link to={listPath(entity, fallbackPath)} className="btn btn-ghost btn-sm">
              {t('record.state.toList')}
            </Link>
          </>
        }
        code={query.error instanceof ApiError ? String(query.error.status) : undefined}
      />
    )
  }
  return null
}

/** Бейдж статуса документа — тем же словарём, что в списке (`value.<сущность>.status.*`). */
export function StatusBadge({ entity, status }: { entity: string; status: string }) {
  return (
    <CellValue
      values={{ status }}
      column="status"
      descriptor={{ name: 'status', type: 'text', sortable: false, filterable: false }}
      entity={entity}
    />
  )
}

/**
 * Полоса этапов из порядка статусов.
 *
 * Этап — позиция документа, а не кнопка: переходы делают действия шапки, где у каждого
 * своё право и своё подтверждение. Пройденный — с галочкой, текущий — `aria-current`.
 */
export function stagesOf(
  order: readonly string[],
  reached: number,
  label: (code: string) => ReactNode,
  meta: (code: string) => ReactNode = () => undefined,
): StageStep[] {
  return order.map((code, index) => ({
    id: code,
    label: label(code),
    meta: meta(code),
    state: index < reached ? 'done' : index === reached ? 'current' : 'future',
  }))
}

/** Отменённый документ — конец полосы, а не ещё один этап: дальше он не движется. */
export function cancelledEnd(cancelled: boolean, label: ReactNode): StageEnd[] {
  return cancelled ? [{ id: 'cancelled', label, tone: 'lost', active: true }] : []
}

/** Момент или слова «ещё не было»: пустой момент — факт о документе, а не пробел в данных. */
export function momentOr(value: unknown, locale: string, fallback: string): string {
  return typeof value === 'string' && value !== '' ? formatMoment(value, locale) : fallback
}

/** Пункт «Скопировать ссылку» — первым в «⋯» у всех документов. */
export function copyLinkItem(t: Translate, onFail: (message: string) => void): MenuAction {
  return {
    id: 'copy-link',
    label: t('record.copyLink'),
    icon: 'link',
    onSelect: () => {
      void navigator.clipboard?.writeText(window.location.href).catch(() => onFail(t('record.copyFailed')))
    },
  }
}

/**
 * Товар строкой: имя и артикул, если сервер их назвал, иначе короткий идентификатор.
 *
 * Подписи приходят в самой строке документа (`productName`, `sku`): раньше их брали из
 * списка остатков, и читатель документа без права на остатки видел только идентификаторы.
 */
export function ProductCell({ id, name, sku }: { id: string; name: string | null | undefined; sku?: string | null }) {
  if (!name) return <span className="mono">{short(id)}</span>
  return (
    <span className="stack" style={{ gap: 0 }}>
      <span>{name}</span>
      <span className="mono muted t-xs">{sku ?? short(id)}</span>
    </span>
  )
}

/**
 * Код ячейки моноширинным, иначе обрезанный идентификатор; нет ячейки — прочерк.
 *
 * Код — из строки документа (`cellCode`), в том числе для ячейки, в которой ничего не
 * лежит: такой ячейки нет в остатках, и прежний путь через них оставлял её без кода.
 */
export function CellCode({ id, code }: { id: string | null | undefined; code?: string | null }) {
  if (!id) return <span className="muted">—</span>
  return <span className="mono">{code ?? short(id)}</span>
}

/**
 * Вкладка «История»: движения товара по документу и обсуждение — лентой, изменения
 * полей — журналом.
 *
 * Лента берётся из `/timeline/{feedType}/{id}`: сервер читает журнал движений и
 * комментарии, нового хранилища нет. Изменения из журнала аудита в ленте тоже есть, но
 * здесь они не повторяются — их полнее показывает журнал ниже (поля, было/стало, листание).
 */
export function HistoryTab({ entity, recordId, feedType }: { entity: string; recordId: string; feedType?: string }) {
  const { t } = useApp()
  return (
    <div className="rec-col" style={{ paddingTop: 14 }}>
      {feedType && <DocumentFeed feedType={feedType} recordId={recordId} />}
      <Panel title={t('record.history.changes')}>
        <HistoryPanel entity={entity} recordId={recordId} />
      </Panel>
    </div>
  )
}

function DocumentFeed({ feedType, recordId }: { feedType: string; recordId: string }) {
  const { t } = useApp()
  const feed = useTimeline(feedType, recordId)
  const items = (feed.data?.events ?? [])
    .filter((event) => event.kind !== 'change')
    .map((event) => feedItemOf(event, t))
  return (
    <Panel title={t('record.wh.feed.title')}>
      {feed.isLoading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : feed.error ? (
        <StateView
          compact
          kind={feed.error instanceof ApiError && feed.error.isForbidden ? 'forbidden' : 'error'}
          title={t('record.wh.feed.error')}
        />
      ) : (
        <Feed
          items={items}
          empty={<StateView compact kind="empty" title={t('record.wh.feed.empty')} />}
          footer={<TimelineMore feed={feed} />}
        />
      )}
    </Panel>
  )
}

/** Переключатель строк таблицы (`.seg`): «все», «с расхождениями»… со счётчиками. */
export function RowFilter<K extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { id: K; label: ReactNode; count?: number }[]
  value: K
  onChange: (id: K) => void
  label: string
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.id} type="button" aria-pressed={option.id === value} onClick={() => onChange(option.id)}>
          {option.label}
          {option.count !== undefined && <span className="muted">{option.count}</span>}
        </button>
      ))}
    </div>
  )
}

/** Сумма колонки строк, которые экран сам показывает (итог таблицы). */
export function sum<T>(items: T[], of: (item: T) => number): number {
  return items.reduce((total, item) => total + of(item), 0)
}
